import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { BottomBar } from "@/components/site/BottomBar";
import { Reveal } from "@/components/site/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field =
  "bg-[#1C1C1C] border-[#3d3d3d] text-white placeholder:text-neutral-500 focus-visible:ring-[#FF4500] focus-visible:ring-offset-0 h-12";

const howItWorks = [
  "Browse available moving jobs and claim the ones that fit your schedule.",
  "Show up, load or unload, and handle the heavy lifting.",
  "Get paid the same day the job is completed.",
];
const requirements = [
  "18 or older",
  "Reliable transportation to job sites",
  "Able to safely lift heavy furniture and boxes",
  "Dependable and professional with customers",
];

const empty = {
  name: "",
  phone: "",
  email: "",
  city: "",
  reliable_transportation: "",
  notes: "",
  agreed_terms: false,
  sms_opt_in: false,
};

export default function EarnWithUs() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.phone.trim()) return toast.error("Please enter your phone number");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Please enter a valid email");
    if (!form.city) return toast.error("Please select your city");
    if (!form.reliable_transportation) return toast.error("Please answer the transportation question");
    if (!form.agreed_terms) return toast.error("You must agree to the Independent Contractor Terms");
    setSubmitting(true);
    try {
      await axios.post(`${API}/mover-applications`, form);
      setDone(true);
      toast.success("Application received!");
    } catch {
      toast.error("Something went wrong. Please try again or text us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="earn-page" className="bg-[#1C1C1C] min-h-screen">
      <Header />
      <main className="pt-32">
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-[#FF4500] font-bold mb-4">
              Independent Movers Wanted
            </p>
            <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl max-w-4xl">
              Earn With Duke Summers Moving
            </h1>
            <p className="mt-6 text-lg text-neutral-300 max-w-2xl leading-relaxed">
              Independent moving pros wanted in Dallas–Fort Worth and Memphis.
              Pick the jobs you want. Get paid the same day.
            </p>
          </Reveal>
        </section>

        {/* How it works + Requirements */}
        <section className="bg-[#262626] border-y border-white/5 py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Reveal>
              <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl mb-8">How It Works</h2>
              <div className="space-y-4">
                {howItWorks.map((s, i) => (
                  <div key={i} className="flex gap-4 border border-[#3d3d3d] bg-[#303030] p-6">
                    <span className="font-display font-extrabold text-2xl text-[#FF4500] leading-none">0{i + 1}</span>
                    <p className="text-neutral-200 leading-relaxed">{s}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl mb-8">Requirements</h2>
              <ul className="space-y-3">
                {requirements.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-neutral-200">
                    <CheckCircle2 className="h-5 w-5 text-[#FF4500] shrink-0 mt-0.5" strokeWidth={1.75} />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        {/* IC Terms */}
        <section className="mx-auto max-w-3xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl mb-8">
              Independent Contractor Terms
            </h2>
            <div className="space-y-5 text-neutral-300 leading-relaxed border-l-2 border-[#FF4500] pl-6">
              <p>
                Movers who join Duke Summers Moving are independent contractors (1099),
                not employees. You choose which jobs to accept, and you may decline any
                job without penalty.
              </p>
              <p>
                <span className="text-white font-semibold">Same-day pay:</span> Movers are
                paid the same day a job is completed, by Cash App, Zelle, or PayPal.
              </p>
              <p>
                Once you accept a job, you are committing to complete it. To cancel an
                accepted job, you must give at least 24 hours' notice. Three late
                cancellations (less than 24 hours' notice) within any 90-day period, or
                any no-show without notice, may result in removal from the Duke Summers
                Moving job board. Documented emergencies are excluded.
              </p>
              <p>
                All disputes between you and Duke Summers Moving will be resolved through
                binding individual arbitration. A full Independent Contractor Agreement
                must be signed before your first job.
              </p>
            </div>
          </Reveal>
        </section>

        {/* Job Alerts by Text */}
        <section className="mx-auto max-w-3xl px-5 sm:px-8 pb-20 sm:pb-28">
          <Reveal>
            <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl mb-8">
              Job Alerts by Text
            </h2>
            <p className="text-neutral-300 leading-relaxed border-l-2 border-[#FF4500] pl-6">
              By opting in, you agree to receive automated text messages from Duke
              Summers Moving about available jobs, schedule updates, and payment.
              Message frequency varies. Message and data rates may apply. Reply STOP
              at any time to opt out, or HELP for help.
            </p>
          </Reveal>
        </section>

        {/* Sign-up form */}
        <section className="bg-[#262626] border-t border-white/5 py-20 sm:py-28">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            {done ? (
              <div className="text-center">
                <CheckCircle2 className="h-16 w-16 text-[#FF4500] mx-auto mb-8" strokeWidth={1.5} />
                <h2 data-testid="earn-success" className="font-display font-bold uppercase tracking-tight text-4xl">
                  Thanks, {form.name.split(" ")[0]}!
                </h2>
                <p className="mt-6 text-neutral-300 text-lg leading-relaxed">
                  Your application has been received. We'll review it and reach out about
                  next steps, including the Independent Contractor Agreement.
                </p>
              </div>
            ) : (
              <>
                <Reveal className="mb-10">
                  <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl">Apply to Join</h2>
                </Reveal>
                <Reveal delay={0.1}>
                  <form onSubmit={submit} data-testid="earn-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-neutral-400">Full name</Label>
                      <Input data-testid="earn-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-neutral-400">Phone</Label>
                      <Input data-testid="earn-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label className="text-xs uppercase tracking-widest text-neutral-400">Email</Label>
                      <Input data-testid="earn-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-neutral-400">City</Label>
                      <Select value={form.city} onValueChange={set("city")}>
                        <SelectTrigger data-testid="earn-city" className={`${field} data-[placeholder]:text-neutral-500`}>
                          <SelectValue placeholder="Select a city" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#303030] border-[#3d3d3d] text-white">
                          {["Dallas–Fort Worth", "Memphis"].map((c) => (
                            <SelectItem key={c} value={c} className="focus:bg-[#FF4500] focus:text-[#141414]">{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-neutral-400">Reliable transportation?</Label>
                      <Select value={form.reliable_transportation} onValueChange={set("reliable_transportation")}>
                        <SelectTrigger data-testid="earn-transport" className={`${field} data-[placeholder]:text-neutral-500`}>
                          <SelectValue placeholder="Select an answer" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#303030] border-[#3d3d3d] text-white">
                          {["Yes", "No"].map((c) => (
                            <SelectItem key={c} value={c} className="focus:bg-[#FF4500] focus:text-[#141414]">{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label className="text-xs uppercase tracking-widest text-neutral-400">Anything else we should know?</Label>
                      <Textarea
                        data-testid="earn-notes"
                        className="bg-[#1C1C1C] border-[#3d3d3d] text-white placeholder:text-neutral-500 focus-visible:ring-[#FF4500] focus-visible:ring-offset-0 min-h-28"
                        value={form.notes}
                        onChange={set("notes")}
                        placeholder="Experience, availability, anything relevant…"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="flex items-start gap-3 border border-[#3d3d3d] bg-[#1C1C1C] p-4 cursor-pointer hover:border-[#FF4500]/50 transition-colors">
                        <Checkbox
                          data-testid="earn-agree"
                          checked={form.agreed_terms}
                          onCheckedChange={(v) => setForm((f) => ({ ...f, agreed_terms: !!v }))}
                          className="mt-0.5 border-[#555] data-[state=checked]:bg-[#FF4500] data-[state=checked]:border-[#FF4500]"
                        />
                        <span className="text-sm text-neutral-200 leading-relaxed">
                          I have read and agree to the Independent Contractor Terms above,
                          including binding individual arbitration.
                        </span>
                      </label>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="flex items-start gap-3 border border-[#3d3d3d] bg-[#1C1C1C] p-4 cursor-pointer hover:border-[#FF4500]/50 transition-colors">
                        <Checkbox
                          data-testid="earn-sms-opt-in"
                          checked={form.sms_opt_in}
                          onCheckedChange={(v) => setForm((f) => ({ ...f, sms_opt_in: !!v }))}
                          className="mt-0.5 border-[#555] data-[state=checked]:bg-[#FF4500] data-[state=checked]:border-[#FF4500]"
                        />
                        <span className="text-sm text-neutral-200 leading-relaxed">
                          I agree to receive automated job alert texts from Duke Summers
                          Moving. Reply STOP to opt out.
                          <span className="block mt-1 text-xs text-neutral-500">
                            Recommended — most jobs are sent by text.
                          </span>
                        </span>
                      </label>
                    </div>
                    <div className="sm:col-span-2">
                      <button
                        type="submit"
                        data-testid="earn-submit"
                        disabled={submitting}
                        className="group inline-flex items-center justify-center gap-2 bg-[#FF4500] text-[#1C1C1C] font-bold px-10 py-4 text-base hover:bg-[#E03C00] transition-colors disabled:opacity-60"
                      >
                        {submitting ? (<><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>) : (<>Apply to Join <ArrowUpRight className="h-5 w-5" /></>)}
                      </button>
                    </div>
                  </form>
                </Reveal>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BottomBar />
    </div>
  );
}
